import { prefersReducedMotion } from '@platform/ui-kit'

/* ------------------------------------------------------------------
   پوستهٔ روشن/تاریکِ پنل — یک جا برای همهٔ دکمه‌ها.

   🔴 ۱۴۰۵/۰۸/۱۴ صاحبِ پروژه: روی گوشی نوارِ پنل در صفحهٔ گفتگو پنهان است و دکمهٔ پوسته
   دیده نمی‌شد؛ حالا خودِ گفتگو کنارِ «+» دکمه دارد و از پنل می‌خواهد پوسته را عوض کند
   (`ChatFrameHost.tsx`، پیامِ `{ carbon: 'theme-toggle' }`). پس تعویض دیگر مالِ دکمهٔ
   نوارِ بالا نیست: همین‌جاست و دکمهٔ نوار هم با `subscribeTheme` همراه می‌شود — وگرنه
   آیکونش کهنه می‌ماند و زدنِ بعدی همان پوستهٔ فعلی را دوباره می‌گذاشت.

   ترجیح در localStorage می‌ماند؛ این داده حساس نیست (ممنوعیتِ بند ۶.۱ فقط دربارهٔ Token است).
   گفتگو خودش از تغییرِ `data-theme` خبردار می‌شود (`ChatFrameHost.tsx`، themeObserver).
   ------------------------------------------------------------------ */

export type Theme = 'light' | 'dark'

const listeners = new Set<(theme: Theme) => void>()

export function currentTheme(): Theme {
  return document.documentElement.dataset.theme === 'dark' ? 'dark' : 'light'
}

export function subscribeTheme(fn: (theme: Theme) => void): () => void {
  listeners.add(fn)
  return () => {
    listeners.delete(fn)
  }
}

/*
  🔴 ۱۴۰۵/۰۸/۱۳ — ضبطِ فریم‌به‌فریم: ۹۰٪ ِ صفحه در یک فریم تیره می‌شد و بقیه (هر عنصرِ دارای
  transition-colors) ۱۲۰ تا ۱۸۰ میلی‌ثانیه بعد، در دو پلهٔ دیگر. پس:
  · ‎.pf-theme-switching‎ (tokens.css) همهٔ transitionها را در لحظهٔ تعویض می‌بندد — همه با هم؛
  · اگر مرورگر view transition دارد، دو حالت ۲۶۰ میلی‌ثانیه در هم محو می‌شوند؛ وگرنه همان تعویضِ یک‌دست.
  ⚠️ شنونده‌ها درونِ همان تابعِ view transition صدا زده می‌شوند (دکمهٔ نوار با flushSync): تصویرِ «بعد»
  همان لحظهٔ برگشتِ تابع گرفته می‌شود و آیکونِ ماه/خورشید باید تا آن لحظه عوض شده باشد.
  کلاس دو فریم بعد برداشته می‌شود تا محاسبهٔ رنگ‌های تازه بی transition تمام شده باشد.
*/
export function switchTheme(next: Theme = currentTheme() === 'light' ? 'dark' : 'light'): void {
  const root = document.documentElement
  const apply = () => {
    root.dataset.theme = next
    try {
      localStorage.setItem('pf-theme', next)
    } catch {
      /* حالتِ خصوصی — همین نشست کافی است */
    }
    listeners.forEach((fn) => fn(next))
  }
  const settle = () => requestAnimationFrame(() => requestAnimationFrame(() => root.classList.remove('pf-theme-switching')))
  root.classList.add('pf-theme-switching')
  if (typeof document.startViewTransition === 'function' && !prefersReducedMotion()) {
    document.startViewTransition(apply).finished.finally(settle)
  } else {
    apply()
    settle()
  }
}
